export default class Cryptographer { 
  
  private _passwordKey: CryptoKey;
  enc = new TextEncoder();
  dec = new TextDecoder();

  private constructor(passwordKey: CryptoKey) { 
    this._passwordKey = passwordKey;
  }

  /**
   * Factory method to create a Cryptographer instance with a derived password key
   */
  public static async create(password: string): Promise<Cryptographer> {
    const passwordKey = await Cryptographer.createPasswordKey(password);
    return new Cryptographer(passwordKey);
  }

  buff_to_base64 = (buff: Iterable<number>) => btoa(
    new Uint8Array(buff).reduce(
      (data, byte) => data + String.fromCharCode(byte), '')
  );

  base64_to_buf = (b64: string) =>
    Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

  private static createPasswordKey = (password: string) => {
    const encoder = new TextEncoder();
    return window.crypto.subtle.importKey("raw", encoder.encode(password), "PBKDF2", false, [
      "deriveKey",
    ]);
  }

  private getPasswordKey(): CryptoKey {
    return this._passwordKey;
  }
  
  deriveKey = (passwordKey: CryptoKey, salt: Uint8Array<ArrayBuffer>, keyUsage: Iterable<KeyUsage>) =>
    window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 250000,
        hash: "SHA-256",
      },
      passwordKey,
      { name: "AES-GCM", length: 256 },
      false,
      keyUsage
    );
  
  public async encryptData(secretData: string) : Promise<string> {
    try {
      const salt = window.crypto.getRandomValues(new Uint8Array(16));
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const passwordKey = this.getPasswordKey();
      const aesKey = await this.deriveKey(passwordKey, salt, ["encrypt"]);
      const encryptedContent = await window.crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: iv,
        },
        aesKey,
        this.enc.encode(secretData)
      );

      const encryptedContentArr = new Uint8Array(encryptedContent);
      const buff = new Uint8Array(
        salt.byteLength + iv.byteLength + encryptedContentArr.byteLength
      );
      buff.set(salt, 0);
      buff.set(iv, salt.byteLength);
      buff.set(encryptedContentArr, salt.byteLength + iv.byteLength);
      const base64Buff = this.buff_to_base64(buff);
      return base64Buff;
    } catch {
      console.error('Encryption failed');
      return "";
    }
  }

  public async decryptData(encryptedData: string) {
    try {
      const encryptedDataBuff = this.base64_to_buf(encryptedData);
      const salt = encryptedDataBuff.slice(0, 16);
      const iv = encryptedDataBuff.slice(16, 16 + 12);
      const data = encryptedDataBuff.slice(16 + 12);
      const passwordKey = this.getPasswordKey();
      const aesKey = await this.deriveKey(passwordKey, salt, ["decrypt"]);
      const decryptedContent = await window.crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: iv,
        },
        aesKey,
        data
      );
      return this.dec.decode(decryptedContent);
    } catch {
      console.error('Decryption failed');
      return "";
    }
  }
}